/**
 * PRODUCT_DEV-890 IP 制限の送信元 IP と、弾いたことを数える仕組み。
 *
 * ## 送信元 IP は API Gateway が見た接続元だけを使う
 *
 * 以前は `x-forwarded-for` の先頭を優先していた（「CloudFront 経由の場合」というコメント付き）。
 * 実際にはどの API も CloudFront を挟まず `execute-api` を直接呼んでいて、このヘッダは
 * **利用者が自由に書ける**。許可 IP を名乗るヘッダを 1 行付けるだけで IP 制限を通れる形だった。
 * `requestContext.http.sourceIp` は「API Gateway へ直接つないできた TCP 接続の送信元」（AWS 公式）で、
 * 利用者には書き換えられない。
 *
 * ## API の前に CloudFront を置いた環境（PRODUCT_DEV-975）
 *
 * そのときの sourceIp は CloudFront になる。`x-forwarded-for` の先頭に戻すのではなく、
 * CloudFront が付ける `CloudFront-Viewer-Address` を使う（CloudFront は利用者が送った
 * `x-forwarded-for` の末尾に追記するだけで、先頭は利用者の値のまま）。
 *
 * ただし `CloudFront-Viewer-Address` も、CloudFront を通らずに来たリクエストなら利用者が書ける。
 * **CloudFront だけが付ける秘密のヘッダ（`x-origin-verify`）が正しいと確かめたときだけ**信じる。
 * 確かめるのは Node 側の `originVerify.ts`（ここは import を足せないので、結果を引数でもらう）。
 *
 * ## ログイン前の経路には IP 制限をかけない（2026-09-14 決定）
 *
 * 一度、`resolve-alias` / `tenant-branding` にテナントの許可 IP の「外側の門」を入れたが外した。
 * ログイン前は誰が来たか分からないので、ユーザ単位の免除（`bypassIpRestriction`）を見られない。
 * 結果、**免除を持つ人が通常ログインでは入れるのに独自ID画面では弾かれる**という体験のずれが出た。
 * IP 制限は各製品の `auth()`（テナント×製品が確定した時点）だけで判定する。
 */

type EventWithSourceIp = {
  requestContext?: { http?: { sourceIp?: string } };
  headers?: Record<string, string | undefined>;
};

/** CloudFront が付ける、利用者の IP とポート（PRODUCT_DEV-975）。API Gateway には小文字で届く。 */
export const VIEWER_ADDRESS_HEADER = "cloudfront-viewer-address";

/**
 * `CloudFront-Viewer-Address` の値から IP だけを取り出す。
 *
 * 値は「IP:ポート」。IPv4 は `198.51.100.10:46532`、IPv6 は角括弧なしで
 * `2001:db8::8a2e:370:7334:46532` のように来る（AWS 公式）。**最後のコロンの後ろがポート**。
 * 念のため角括弧つき（`[2001:db8::1]:443`）も受ける。形が崩れていれば undefined。
 */
export const parseViewerAddress = (value: string | undefined): string | undefined => {
  const v = value?.trim();
  if (!v) return undefined;
  const bracketed = /^\[([0-9a-fA-F:.]+)\]:(\d{1,5})$/.exec(v);
  if (bracketed) return bracketed[1];
  const idx = v.lastIndexOf(":");
  if (idx <= 0) return undefined;
  const ip = v.slice(0, idx);
  const port = v.slice(idx + 1);
  if (!/^\d{1,5}$/.test(port)) return undefined;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) return ip;
  // IPv6 はコロンを 2 つ以上含む（IPv4 の後ろに `:ポート` だけ付いた形と区別する）
  if (/^[0-9a-fA-F:.]+$/.test(ip) && ip.split(":").length >= 3) return ip;
  return undefined;
};

/**
 * 送信元 IP。
 *
 * - 既定: API Gateway が見た TCP 接続の送信元（`requestContext.http.sourceIp`）
 * - `trustedViaCloudFront: true`（秘密のヘッダを確かめ済み）: `CloudFront-Viewer-Address` の IP。
 *   このときヘッダが無い・壊れているなら **sourceIp には戻さず** undefined を返す
 *   （CloudFront の IP を許可リストと突き合わせても意味が無く、取れないなら 403 にする）
 */
export const sourceIpOf = (
  event: EventWithSourceIp,
  opts: { trustedViaCloudFront?: boolean } = {},
): string | undefined =>
  opts.trustedViaCloudFront
    ? parseViewerAddress(event.headers?.[VIEWER_ADDRESS_HEADER])
    : event.requestContext?.http?.sourceIp || undefined;

/**
 * IP 制限で弾いたことを表す、**機械が読む**値（PRODUCT_DEV-902）。
 *
 * 403 の本文の `reason` に入る。画面はこれを見て案内を出し分ける。
 * `message` は人が読む自由文で、**文言の一致で判定しないこと**（変えた瞬間に案内が壊れる）。
 *
 * ⚠ **このファイルに import を足さないこと。** ブラウザ側（`shared-ui`）からも読むので、
 * Node 専用のものが混ざると画面のビルドが壊れる。
 */
export const IP_NOT_ALLOWED_REASON = "IP_NOT_ALLOWED";

export const IP_DENIED_METRIC = "IpDenied";

/**
 * 件数の書き出し先。env ごとに分ける（dev の試験で prod の件数を汚さない）。
 *
 * ⚠ 本番の Lambda の `ENV` は `"prd"`（`utils/lambda.ts`）だが、名前空間は `-prod` にそろえる。
 * 手順書（error-watch）や他のメトリクス（`Unizo/Alerting-prod`）はすべて `prod` で読むので、
 * そのまま使うと**本番だけ `Umeee/Security-prd` に書かれ、読む側には0件に見える**
 * （2026-09-17 の本番確認で実際にそうなっていた。PRODUCT_DEV-913）。
 * `utils/env.ts` の `isProd` を使わないのは、このファイルに import を足せないため（上の注意）。
 */
export const ipDeniedNamespace = (env: string): string =>
  `Umeee/Security-${env === "prd" ? "prod" : env}`;

/** どこで弾いたか。いまは各製品の認可（`auth()`）だけ。 */
export type IpDeniedStage = "authMiddleware";

/**
 * 弾いた 1 件を EMF の 1 行にする。
 *
 * 次元は `Stage` だけ（テナントや IP を次元にするとメトリクスが増え続ける）。
 * どのテナント・どの送信元かは、同じ時刻に出る warn `IP address not allowed` で引く。
 */
export const buildIpDeniedEmf = (opts: {
  env: string;
  productId: string;
  stage: IpDeniedStage;
  timestamp?: number;
}): string =>
  JSON.stringify({
    _aws: {
      Timestamp: opts.timestamp ?? Date.now(),
      CloudWatchMetrics: [
        {
          Namespace: ipDeniedNamespace(opts.env),
          Dimensions: [["Stage"]],
          Metrics: [{ Name: IP_DENIED_METRIC, Unit: "Count" }],
        },
      ],
    },
    Stage: opts.stage,
    [IP_DENIED_METRIC]: 1,
    // 次元ではない（ログで引くためだけに残す）
    productId: opts.productId,
  });

/**
 * 弾いた 1 件を数える。Slack には出さない（週次の error-watch で読む）。
 * `ENV` が無い実行（ローカル・テスト）では何も出さない。
 */
export const recordIpDenied = (opts: {
  productId: string;
  stage: IpDeniedStage;
}): void => {
  const env = process.env.ENV;
  if (!env) return;
  console.log(buildIpDeniedEmf({ env, ...opts }));
};
